import { Alert, Spinner } from '@amzn/awsui-components-react-v3';
import React, { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { AJAX_CALL_ERROR } from '../../../common/constants/grimsby';

import { ActivityData } from '../../interfaces/activity';
import {
    getSelectedActivity,
    selectError as selectActivityError,
    selectIsLoading as selectIsActivityLoading,
    selectSelectedActivity,
} from '../../store/slices/selectedActivitySlice';
import OpportunityForm from './OpportunityForm';
import OrderServiceBypassModal from '../Activity/Create/OrderServiceBypassModal';

const OpportunityDetails = () => {
    const match = useRouteMatch<{ id: string }>();
    const isLoading = useSelector(selectIsActivityLoading);
    const error: any = useSelector(selectActivityError);
    const activity: ActivityData | undefined = useSelector(
        selectSelectedActivity,
    )?.activity;

    const dispatch = useDispatch();

    // OrderService bypass modal state - managed at parent level to persist across child remounts
    const [showBypassModal, setShowBypassModal] = useState(false);
    const [pendingActivityData, setPendingActivityData] =
        useState<ActivityData | null>(null);
    const [bypassCallback, setBypassCallback] = useState<
        ((bypassReason: string) => void) | null
    >(null);

    useEffect(() => {
        dispatch(getSelectedActivity(match.params.id));
    }, [dispatch, match.params.id]);

    const handleShowBypassModal = useCallback(
        (
            activityData: ActivityData,
            onBypass: (bypassReason: string) => void,
        ) => {
            setPendingActivityData(activityData);
            setBypassCallback(() => onBypass);
            setShowBypassModal(true);
        },
        [],
    );

    const handleCancelBypass = useCallback(() => {
        setShowBypassModal(false);
        setPendingActivityData(null);
        setBypassCallback(null);
    }, []);

    const handleBypass = useCallback(
        (bypassReason: string) => {
            if (bypassCallback) {
                bypassCallback(bypassReason);
            }
            setShowBypassModal(false);
        },
        [bypassCallback],
    );

    if (error) {
        return (
            <Alert
                data-testid="EditDeliveryDetailsError"
                header="Error"
                type="error"
            >
                {AJAX_CALL_ERROR}
            </Alert>
        );
    } else if (!isLoading && !!activity) {
        return (
            <>
                <OpportunityForm
                    initialFormState={activity}
                    onShowBypassModal={handleShowBypassModal}
                />
                <OrderServiceBypassModal
                    visible={showBypassModal}
                    onCancel={handleCancelBypass}
                    onBypass={handleBypass}
                    isLoading={isLoading}
                />
            </>
        );
    } else {
        return <Spinner data-testid="EditDeliveryDetailsSpinner" />;
    }
};

export default OpportunityDetails;
